import { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, ChevronRight, ExternalLink, Flame } from "lucide-react";
import HiddenTruthsPanel from "./HiddenTruthsPanel";

export default function StoryCard({ story, index, isBookmarked, onToggleBookmark, requireAuth }) {
  const [expanded, setExpanded] = useState(false);

  const num = String(index + 1).padStart(2, "0");

  return (
    <article
      data-testid={`story-card-${story.id}`}
      className="border-b border-stone-300 py-8 group"
    >
      <div className="grid grid-cols-12 gap-4">
        {/* Left number + meta */}
        <div className="col-span-12 md:col-span-2 flex md:flex-col gap-3 md:gap-2 items-start">
          <div className="font-serif-head text-5xl md:text-6xl text-[#E60000] leading-none">
            {num}
          </div>
          <div className="flex items-center gap-1 font-mono-head text-[10px] uppercase tracking-widest text-stone-500">
            <Flame className="w-3 h-3" /> Heat {story.heat_score ?? 5}
          </div>
        </div>

        {/* Body */}
        <div className="col-span-12 md:col-span-10">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center border border-stone-300 px-2 py-1 text-[10px] font-mono-head uppercase tracking-widest text-stone-700 bg-stone-100">
              {story.source}
            </span>
            <span className="font-mono-head text-[10px] uppercase tracking-widest text-stone-900 hover:text-[#E60000]">
              · {story.category}
            </span>
            {story.published && (
              <span className="font-mono-head text-[10px] uppercase tracking-widest text-stone-400">
                · {String(story.published).slice(0, 25)}
              </span>
            )}
          </div>

          <Link
            to={`/story/${story.id}`}
            data-testid={`story-title-link-${story.id}`}
            className="block group/title"
          >
            <h2 className="font-serif-head text-2xl sm:text-3xl text-stone-900 leading-tight mb-3 group-hover/title:text-[#E60000] transition-colors">
              {story.title}
            </h2>
          </Link>

          <p className="text-base leading-relaxed text-stone-700 mb-4 line-clamp-3">
            {story.summary}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid={`expand-truth-${story.id}`}
              onClick={() => setExpanded((e) => !e)}
              className="inline-flex items-center gap-2 bg-stone-900 text-stone-50 font-mono-head text-xs uppercase tracking-widest px-4 py-2.5 hover:bg-[#E60000] transition-colors"
            >
              <span className="w-2 h-2 bg-[#E60000] group-hover:bg-stone-50 animate-pulse rounded-full" />
              {expanded ? "Collapse Analysis" : "Reveal Hidden Truths"}
              <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
            </button>

            <a
              href={story.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`source-link-${story.id}`}
              className="inline-flex items-center gap-1.5 font-mono-head text-xs uppercase tracking-widest text-stone-700 hover:text-stone-900 border border-stone-300 px-3 py-2.5 hover:border-stone-900 transition-colors"
            >
              Source <ExternalLink className="w-3 h-3" />
            </a>

            <button
              data-testid={`bookmark-btn-${story.id}`}
              onClick={() => (requireAuth ? requireAuth() : onToggleBookmark(story.id))}
              className="inline-flex items-center gap-1.5 font-mono-head text-xs uppercase tracking-widest text-stone-700 hover:text-[#E60000] border border-stone-300 px-3 py-2.5 hover:border-[#E60000] transition-colors"
            >
              {isBookmarked ? <BookmarkCheck className="w-3.5 h-3.5 text-[#E60000]" /> : <Bookmark className="w-3.5 h-3.5" />}
              {isBookmarked ? "Saved" : "Save"}
            </button>
          </div>

          {expanded && (
            <HiddenTruthsPanel storyId={story.id} />
          )}
        </div>
      </div>
    </article>
  );
}
